/**
 * VÉRAULT Member Suite — useArrivalFocus (Wave 26.27 · Phase 3e).
 * ───────────────────────────────────────────────────────────────
 * Hash-triggered gentle scroll + 2.4s gold pulse on the matched
 * element. Wired across the Experience surfaces so any deep link
 * landing here (Itinerary → Essentials, Overview → Itinerary, etc.)
 * gives the member a calm visual handshake instead of a silent jump.
 *
 * Usage:
 *   useArrivalFocus();           // reads window.location.hash
 *   useArrivalFocus("balances"); // forces a specific target
 *
 * Convention:
 *   - Match by element id (must equal the hash without "#").
 *   - Element gets the class "verault-arrival-focus" for 2.4s.
 *   - Element scrolls smoothly to center (or "start" on small viewports).
 */
import React from "react";

const ARRIVAL_CLASS = "verault-arrival-focus";
const PULSE_MS = 2400;

export function useArrivalFocus(forcedId = null) {
  React.useEffect(() => {
    const target = forcedId || (typeof window !== "undefined" ? (window.location.hash || "").replace(/^#/, "") : "");
    if (!target) return undefined;

    let timer = null;
    let raf = null;

    const apply = () => {
      const el = document.getElementById(target);
      if (!el) {
        // Try again on next frame — the target may not be mounted yet.
        raf = requestAnimationFrame(apply);
        return;
      }
      // Smooth scroll. Center on tall viewports, start on short ones
      // (so the eyebrow stays readable on phones).
      const isShort = typeof window !== "undefined" && window.innerHeight < 720;
      el.scrollIntoView({ behavior: "smooth", block: isShort ? "start" : "center" });
      el.classList.add(ARRIVAL_CLASS);
      timer = setTimeout(() => {
        el.classList.remove(ARRIVAL_CLASS);
      }, PULSE_MS);
    };

    // Run after first paint so layout is stable.
    raf = requestAnimationFrame(apply);

    return () => {
      if (timer) clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
      // Best-effort cleanup if we navigate away mid-pulse.
      const el = target && document.getElementById(target);
      if (el) el.classList.remove(ARRIVAL_CLASS);
    };
  }, [forcedId]);
}

export default useArrivalFocus;
